'use client';

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import myLogo from "../Assets/NordicRoute.svg";
import {
  Dialog,
  DialogPanel,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Popover,
  PopoverButton,
  PopoverGroup,
  PopoverPanel,
} from '@headlessui/react';

import {
  Bars3Icon,
  ChartPieIcon,
  CursorArrowRaysIcon,
  SquaresPlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import { ChevronDownIcon, PhoneIcon } from '@heroicons/react/20/solid';

const products = [
  {
    name: 'Godstransport',
    description: 'Säker och pålitlig transport av gods inom Sverige och Norden.',
    href: '/services/godstransport',
    icon: ChartPieIcon,
  },
  {
    name: 'Expressleveranser',
    description: 'Snabba leveranser när tiden är avgörande.',
    href: '/services/expressleveranser',
    icon: CursorArrowRaysIcon,
  },
  {
    name: 'Specialtransporter',
    description: 'Skräddarsydda lösningar för känsligt eller skrymmande gods.',
    href: '/services/specialtransporter',
    icon: SquaresPlusIcon,
  },
];

const callsToAction = [
  { name: 'Kontakta oss', href: '/contact', icon: PhoneIcon },
];

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header
    role="banner"
    className="fixed inset-x-0 top-0 z-50 h-24 bg-gradient-to-b from-white/70 via-white/40 to-transparent backdrop-blur-xl"
  >
  
      <nav
        aria-label="Huvudnavigation"
        className="mx-auto flex max-w-7xl items-center justify-between p-6 lg:px-8"
      >
        {/* LOGO */}
        <div className="flex lg:flex-1">
          <Link to="/" className="-m-1.5 p-1.5" aria-label="Gå till startsidan">
            <img
              alt="Nordic Route logotyp"
              src={myLogo}
              className="h-20 w-auto"
            />
          </Link>
        </div>

        {/* MOBILE MENU BUTTON */}
        <div className="flex lg:hidden">
          <button
            type="button"
            aria-label="Öppna mobilmeny"
            onClick={() => setMobileMenuOpen(true)}
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
          >
            <Bars3Icon aria-hidden="true" className="size-6" />
          </button>
        </div>

        {/* DESKTOP MENU */}
        <PopoverGroup className="hidden lg:flex lg:gap-x-12">

          {/* DROPDOWN – TJÄNSTER */}
          <Popover className="relative">
            <PopoverButton
              className="flex items-center gap-x-1 text-sm font-semibold text-gray-900"
              aria-label="Öppna meny för tjänster"
            >
              Tjänster
              <ChevronDownIcon aria-hidden="true" className="size-5 text-gray-400" />
            </PopoverButton>

            <PopoverPanel
              transition
              className="absolute left-1/2 z-10 mt-3 w-screen max-w-md -translate-x-1/2 overflow-hidden rounded-3xl bg-white shadow-lg outline outline-1 outline-gray-900/5"
            >
              <div className="p-4">
                {products.map((item) => (
                  <div
                    key={item.name}
                    className="group relative flex items-center gap-x-6 rounded-lg p-4 hover:bg-gray-50"
                    role="menuitem"
                  >
                    <div className="flex size-11 items-center justify-center rounded-lg bg-gray-50 group-hover:bg-white">
                      <item.icon aria-hidden="true" className="size-6 text-gray-600 group-hover:text-indigo-600" />
                    </div>
                    <div className="flex-auto">
                      <Link to={item.href} className="block font-semibold text-gray-900">
                        {item.name}
                        <span className="absolute inset-0" aria-hidden="true" />
                      </Link>
                      <p className="mt-1 text-gray-600">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 divide-x divide-gray-900/5 bg-gray-50">
                {callsToAction.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="flex items-center justify-center gap-x-2.5 p-3 text-sm font-semibold text-gray-900 hover:bg-gray-100"
                    aria-label="Gå till kontaktsidan"
                  >
                    <item.icon aria-hidden="true" className="size-5 text-gray-400" />
                    {item.name}
                  </Link>
                ))}
              </div>
            </PopoverPanel>
          </Popover>

          <Link to="/about" className="text-sm font-semibold text-gray-900">
            Om oss
          </Link>
        </PopoverGroup>

        {/* DESKTOP CTA */}
        <div className="hidden lg:flex lg:flex-1 lg:justify-end">
          <Link to="/contact" className="text-sm font-semibold text-gray-900">
            Kontakta oss <span aria-hidden="true">&rarr;</span>
          </Link>
        </div>
      </nav>

      {/* MOBILE MENU */}

<Dialog
  open={mobileMenuOpen}
  onClose={setMobileMenuOpen}
  aria-label="Mobil meny"
  className="lg:hidden"
>
  <div className="fixed inset-0 z-50" />

  <DialogPanel className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white p-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">

    {/* HEADER */}
    <div className="flex items-center justify-between">
      <Link
        to="/"
        onClick={() => setMobileMenuOpen(false)}
        className="-m-1.5 p-1.5"
        aria-label="Gå till startsidan"
      >
        <img
          alt="Nordic Route logotyp"
          src={myLogo}
          className="h-8 w-auto"
        />
      </Link>

      <button
        type="button"
        aria-label="Stäng mobilmeny"
        onClick={() => setMobileMenuOpen(false)}
        className="-m-2.5 rounded-md p-2.5 text-gray-700"
      >
        <XMarkIcon aria-hidden="true" className="size-6" />
      </button>
    </div>

    {/* MENU CONTENT */}
    <div className="mt-6 flow-root">
      <div className="-my-6 divide-y divide-gray-500/10">

        <div className="space-y-2 py-6">

          {/* TJÄNSTER DROPDOWN */}
          <Disclosure as="div">
            <DisclosureButton className="group flex w-full items-center justify-between rounded-lg py-2 pr-3.5 pl-3 text-base font-semibold text-gray-900 hover:bg-gray-50">
              Tjänster
              <ChevronDownIcon aria-hidden="true" className="size-5 group-data-[open]:rotate-180" />
            </DisclosureButton>

            <DisclosurePanel className="mt-2 space-y-2">
              {[...products, ...callsToAction].map((item) => (
                <DisclosureButton
                  key={item.name}
                  as={Link}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}  // ⭐ STÄNG MENYN
                  className="block rounded-lg py-2 pr-3 pl-6 text-sm font-semibold text-gray-900 hover:bg-gray-50"
                >
                  {item.name}
                </DisclosureButton>
              ))}
            </DisclosurePanel>
          </Disclosure>

          <Link
            to="/about"
            onClick={() => setMobileMenuOpen(false)}  // ⭐ STÄNG MENYN
            className="block rounded-lg px-3 py-2 text-base font-semibold text-gray-900 hover:bg-gray-50"
          >
            Om oss
          </Link>

          <Link
            to="/contact"
            onClick={() => setMobileMenuOpen(false)}  // ⭐ STÄNG MENYN
            className="block rounded-lg px-3 py-2 text-base font-semibold text-gray-900 hover:bg-gray-50"
          >
            Kontakt
          </Link>
        </div>

        <div className="py-6">
          <Link
            to="/contact"
            onClick={() => setMobileMenuOpen(false)}  // ⭐ STÄNG MENYN
            className="block rounded-lg px-3 py-2.5 text-base font-semibold text-gray-900 hover:bg-gray-50"
          >
            Kontakta oss
          </Link>
        </div>
      </div>
    </div>

  </DialogPanel>
</Dialog>

    </header>
  );
};

export default Navbar;
